import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { getUserQuestions } from "../services/api/question";
import { Question } from "../types/Question";
import {
    getCompletionPercent,
    getMissingUserFieldLabels,
    getQuestionsProgress,
    isProfileComplete as checkProfileComplete,
    UserProfileFields,
} from "../utils/profileCompletion";

export type UseProfileCompletionResult = {
    /** true si profil user (firstname, lastname, pseudo) + toutes les questions ont une réponse */
    isProfileComplete: boolean;
    /** true pendant le chargement des questions (GET /v2/users/questions) */
    isLoading: boolean;
    /** Erreur éventuelle lors du fetch des questions (profil considéré incomplet) */
    error: boolean;
    /** Libellés des champs user manquants (ex. ["Prénom", "Nom"]) */
    missingUserFieldLabels: string[];
    /** Nombre de questions avec réponse / total questions */
    questionsAnswered: number;
    questionsTotal: number;
    /** Pourcentage de complétion 0–100 (3 champs user + questions) */
    completionPercent: number;
};

/**
 * Hook centralisé pour la complétion du profil.
 * - User : AuthContext.user (firstname, lastname, pseudo)
 * - Questions : GET /v2/users/questions (answer non null et non vide pour chaque entrée)
 * Réutilisable sur /trades, layout, ou autre page.
 */
export function useProfileCompletion(): UseProfileCompletionResult {
    const { user } = useContext(AuthContext)!;
    const [questions, setQuestions] = useState<Question[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchQuestions = async () => {
            setError(false);
            try {
                const data = await getUserQuestions();
                if (!cancelled) setQuestions(data ?? []);
            } catch {
                if (!cancelled) {
                    setError(true);
                    setQuestions([]);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        fetchQuestions();
        return () => {
            cancelled = true;
        };
    }, []);

    const userFields: UserProfileFields | undefined = user
        ? {
              firstname: user.firstname,
              lastname: user.lastname,
              pseudo: user.pseudo,
          }
        : undefined;

    const isProfileComplete =
        !isLoading && !error && checkProfileComplete(userFields, questions);

    const missingUserFieldLabels = getMissingUserFieldLabels(userFields);
    const { answered: questionsAnswered, total: questionsTotal } =
        getQuestionsProgress(questions);
    const completionPercent =
        isLoading || error ? 0 : getCompletionPercent(userFields, questions);

    return {
        isProfileComplete,
        isLoading,
        error,
        missingUserFieldLabels,
        questionsAnswered,
        questionsTotal,
        completionPercent,
    };
}
